import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Settings,
  Key,
  Check,
  X,
  Zap,
  Eye,
  EyeOff,
  ExternalLink,
  Cpu,
  TestTube
} from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

const AdminAIConfig = () => {
  const { api, user } = useAuth();
  const [config, setConfig] = useState({
    has_openrouter_key: false,
    openrouter_model: 'anthropic/claude-sonnet-4-6',
    updated_at: null,
    updated_by: null
  });
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Determine if user is authorized
  const isAuthorized = user?.role === 'super_admin';

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/ai-config');
      setConfig(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acceso denegado');
      } else {
        toast.error('Error al cargar la configuración');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchConfig();
    } else {
      setLoading(false);
    }
  }, [isAuthorized]);

  // Check authorization after hooks
  if (!isAuthorized && !loading) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Por favor ingresa una API Key');
      return;
    }

    setSaving(true);
    try {
      await api.put('/admin/ai-config', {
        openrouter_api_key: apiKey,
        openrouter_model: config.openrouter_model
      });
      toast.success('Configuración guardada correctamente');
      setApiKey('');
      fetchConfig();
    } catch (error) {
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await api.post('/admin/ai-config/test');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al probar la conexión');
    } finally {
      setTesting(false);
    }
  };

  const models = [
    { id: 'anthropic/claude-sonnet-4-6', name: 'Claude Sonnet 4.6', description: 'Mejor calidad/precio para copywriting', recommended: true },
    { id: 'anthropic/claude-opus-4-6', name: 'Claude Opus 4.6', description: 'El más potente, más lento y caro' },
    { id: 'anthropic/claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5', description: 'Más rápido y barato, calidad suficiente' },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl space-y-6" data-testid="admin-ai-config-page">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-text-main">Configuración de IA</h1>
              <p className="text-text-secondary text-sm">
                Conecta tu API de OpenRouter para generar copy con IA
              </p>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <div className={`p-4 rounded-xl border ${config.has_openrouter_key ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
          <div className="flex items-center gap-3">
            {config.has_openrouter_key ? (
              <>
                <Check className="w-5 h-5 text-green-400" />
                <div>
                  <p className="font-medium text-green-400">API Key Configurada</p>
                  <p className="text-sm text-text-muted">
                    Modelo activo: <span className="text-text-main font-mono">{config.openrouter_model}</span>
                  </p>
                </div>
              </>
            ) : (
              <>
                <X className="w-5 h-5 text-yellow-400" />
                <div>
                  <p className="font-medium text-yellow-400">Sin API Key</p>
                  <p className="text-sm text-text-muted">Configura tu API Key de OpenRouter para activar la generación de IA</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* API Key Section */}
        <div className="bg-surface border border-border-subtle rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
              <Key className="w-5 h-5 text-brand-500" />
            </div>
            <div>
              <h2 className="font-medium text-text-main">API Key de OpenRouter</h2>
              <p className="text-sm text-text-secondary">Tu API Key se almacena de forma segura y encriptada</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-text-secondary">
                {config.has_openrouter_key ? 'Actualizar API Key' : 'Ingresa tu API Key'}
              </Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={config.has_openrouter_key ? '••••••••••••••••••••' : 'sk-or-v1-...'}
                    className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted pr-10 font-mono"
                    data-testid="openrouter-api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main"
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  onClick={handleSave}
                  disabled={saving || !apiKey.trim()}
                  className="bg-brand-500 hover:bg-brand-hover text-white"
                  data-testid="save-api-key-btn"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <a
                href="https://openrouter.ai/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-brand-500 hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Obtener API Key en OpenRouter
              </a>
              
              {config.has_openrouter_key && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={testing}
                  className="border-border-subtle text-text-secondary hover:text-text-main"
                  data-testid="test-connection-btn"
                >
                  <TestTube className="w-4 h-4 mr-2" />
                  {testing ? 'Probando...' : 'Probar Conexión'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Model Selection */}
        <div className="bg-surface border border-border-subtle rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="font-medium text-text-main">Modelo de IA</h2>
              <p className="text-sm text-text-secondary">Selecciona el modelo para generar el copy</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {models.map((model) => (
              <button
                key={model.id}
                onClick={async () => {
                  setConfig({ ...config, openrouter_model: model.id });
                  if (config.has_openrouter_key) {
                    try {
                      await api.put('/admin/ai-config', { openrouter_model: model.id });
                      toast.success(`Modelo cambiado a ${model.name}`);
                    } catch (error) {
                      toast.error('Error al cambiar modelo');
                    }
                  }
                }}
                className={`p-4 rounded-xl border text-left transition-all ${
                  config.openrouter_model === model.id
                    ? 'bg-brand/10 border-brand/50'
                    : 'bg-app-bg border-border-subtle hover:border-brand/30'
                }`}
                data-testid={`model-${model.id.replace('/', '-')}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-medium ${config.openrouter_model === model.id ? 'text-brand' : 'text-text-main'}`}>
                    {model.name}
                  </span>
                  {model.recommended && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                      Recomendado
                    </span>
                  )}
                </div>
                <p className="text-sm text-text-muted">{model.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Last Updated */}
        {config.updated_at && (
          <p className="text-sm text-text-muted text-center">
            Última actualización: {new Date(config.updated_at).toLocaleString('es-ES')} por {config.updated_by}
          </p>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminAIConfig;
